/**
 * ChainTable — the Workspace view's left column: Calls | Strike | Puts.
 *
 * Click a Bid to SELL, an Ask to BUY (the touch you'd trade against), or hover a side
 * and press S / B. ITM halves are tinted, the index sits between the strikes it falls
 * between, legs already in the basket are badged, and a faint bar behind the outer
 * column shows open interest relative to the busiest strike on that side.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Tooltip } from '@mui/material';
import { WsChain, WsRow, WsQuote, atmOf } from './wsData';
import { WsLeg, OptType, Side } from './wsMath';
import { WsColors, fmtPx, fmtStrike, fmtNum, expLabel, compactLots } from './wsUi';

interface Props {
  chain: WsChain | null;
  legs: WsLeg[];
  side: 'both' | 'CE' | 'PE';
  cols: 'iv' | 'greeks' | 'none';
  range: number;
  c: WsColors;
  onAdd: (row: WsRow, t: OptType, action: Side) => void;
  onHover: (h: { row: WsRow; t: OptType } | null) => void;
  /** bump to re-centre on ATM */
  recentre: number;
  /** Chart a contract in the strip (click its Mark). */
  onChart?: (symbol: string) => void;
}

const ROW_H = 30;


const ChainTable: React.FC<Props> = ({ chain, legs, side, cols, range, c, onAdd, onHover, recentre, onChart }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const idxRef = useRef<HTMLDivElement>(null);
  const rowsAll = useMemo(() => chain?.chain?.rows || [], [chain]);
  const spot = chain?.spot || 0;
  const iso = chain?.chain?.iso || '';
  const { strike: atm } = atmOf(rowsAll, spot);

  const rows = useMemo(() => {
    if (!range || atm == null) return rowsAll;
    const i = rowsAll.findIndex(r => r.strike === atm);
    return rowsAll.slice(Math.max(0, i - range), i + range + 1);
  }, [rowsAll, range, atm]);

  const maxOi = useMemo(() => ({
    CE: Math.max(1, ...rows.map(r => r.ce?.oi || 0)),
    PE: Math.max(1, ...rows.map(r => r.pe?.oi || 0)),
  }), [rows]);

  const legBadges = useMemo(() => {
    const m = new Map<string, string>();
    legs.forEach(l => {
      if (l.expiry !== iso) return;
      const k = `${l.optionType}|${l.strike}`;
      m.set(k, (m.get(k) ? m.get(k) + ' ' : '') + `${l.action[0]}${l.lots > 1 ? compactLots(l.lots) : ''}`);
    });
    return m;
  }, [legs, iso]);

  // Centre the index row on first data for an expiry, and on demand.
  const centredFor = useRef<string>('');
  useEffect(() => {
    const key = `${iso}|${recentre}|${range}`;
    if (!rows.length || centredFor.current === key) return;
    const sc = scrollRef.current, ix = idxRef.current;
    if (sc && ix) {
      sc.scrollTop = Math.max(0, ix.offsetTop - sc.clientHeight / 2 + ROW_H / 2);
      centredFor.current = key;
    }
  }, [iso, rows.length, recentre, range]);

  if (!chain?.chain) {
    return <Box sx={{ p: 2, color: c.dim, fontSize: 12 }}>Loading chain…</Box>;
  }

  const showCE = side !== 'PE', showPE = side !== 'CE';
  const extra = cols !== 'none';
  const extraLabel = cols === 'iv' ? 'IV%' : 'Δ';
  const extraOf = (q: WsQuote | null) => (cols === 'iv' ? (q?.iv ? (q.iv * 100).toFixed(1) : '—') : fmtNum(q?.delta ?? null, 2));

  // grid columns: [extra] mark bid ask | strike | bid ask mark [extra]
  const ceCols = showCE ? (extra ? 4 : 3) : 0;
  const peCols = showPE ? (extra ? 4 : 3) : 0;
  const tmpl = `${Array(ceCols).fill('minmax(0,1fr)').join(' ')} 84px ${Array(peCols).fill('minmax(0,1fr)').join(' ')}`;

  const cell = (content: React.ReactNode, sx: any = {}, key?: string) => (
    <Box key={key} sx={{ px: 0.6, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...sx }}>{content}</Box>
  );
  const clickCell = (q: WsQuote | null, row: WsRow, t: OptType, action: Side, v: number | null) => {
    const can = v != null && v > 0;
    return (
      <Tooltip key={`${t}${action}`} title={can ? `${action === 'SELL' ? 'Sell' : 'Buy'} ${fmtStrike(row.strike)} ${t} at ${fmtPx(v)}` : 'No quote'} enterDelay={600} disableInteractive>
        <Box component="button" type="button" disabled={!can}
          onClick={() => can && onAdd(row, t, action)}
          sx={{
            all: 'unset', cursor: can ? 'pointer' : 'default', px: 0.6, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
            color: action === 'SELL' ? c.green : c.red, borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            '&:hover': can ? { bgcolor: action === 'SELL' ? c.greenBg : c.redBg, outline: `1px solid ${action === 'SELL' ? c.green : c.red}` } : {},
          }}>
          {fmtPx(v)}
        </Box>
      </Tooltip>
    );
  };

  const sideCells = (row: WsRow, t: OptType) => {
    const q = t === 'CE' ? row.ce : row.pe;
    const itm = t === 'CE' ? row.strike < spot : row.strike > spot;
    const oiW = Math.min(100, ((q?.oi || 0) / maxOi[t]) * 100);
    const ext = extra ? cell(
      <>
        <Box component="span" sx={{ position: 'absolute', top: 6, bottom: 6, [t === 'CE' ? 'left' : 'right']: 0, width: `${oiW}%`, bgcolor: t === 'CE' ? c.greenBg : c.redBg, borderRadius: 0.5, pointerEvents: 'none' }} />
        <Box component="span" sx={{ position: 'relative' }}>{extraOf(q)}</Box>
      </>, { position: 'relative', color: c.dim }, `${t}x`) : null;
    const mark = q?.symbol && onChart ? (
      <Tooltip key={`${t}m`} title={`Chart ${q.symbol} in the strip`} enterDelay={600} disableInteractive>
        <Box component="button" type="button" onClick={() => onChart(q.symbol)}
          sx={{ all: 'unset', cursor: 'pointer', px: 0.6, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: c.text, borderRadius: 0.5,
            '&:hover': { textDecoration: 'underline', bgcolor: c.hover } }}>{fmtPx(q?.mark ?? null)}</Box>
      </Tooltip>
    ) : cell(fmtPx(q?.mark ?? null), { fontWeight: 600, color: c.text }, `${t}m`);
    const bid = clickCell(q, row, t, 'SELL', q?.bid ?? null);
    const ask = clickCell(q, row, t, 'BUY', q?.ask ?? null);
    const cells = t === 'CE' ? [ext, mark, bid, ask] : [bid, ask, mark, ext];
    return (
      <Box sx={{ display: 'contents' }}>
        {cells.filter(Boolean).map((el, i) => (
          <Box key={i} onMouseEnter={() => onHover({ row, t })}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', bgcolor: itm ? c.itm : 'transparent', height: ROW_H }}>
            {el}
          </Box>
        ))}
      </Box>
    );
  };

  // Inline beside the strike (never on top of it), shrinking before it can clip the number.
  const badgeSx = (_side: 'left' | 'right') => ({
    flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, px: 0.3, borderRadius: 0.5,
    fontSize: 8.5, fontWeight: 800, lineHeight: 1.5, bgcolor: c.amber, color: '#111',
  });

  // Insert the index marker between the strikes the spot falls between.
  const idxAt = rows.findIndex(r => r.strike >= spot);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, fontSize: 12 }} onMouseLeave={() => onHover(null)}>
      {/* side headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: tmpl, px: 0.5, pt: 0.6, pb: 0.3, fontSize: 10.5, letterSpacing: 0.6, fontWeight: 700 }}>
        {showCE && <Box sx={{ gridColumn: `span ${ceCols}`, color: c.call }}>● CALLS</Box>}
        <Box sx={{ textAlign: 'center', color: c.amber, whiteSpace: 'nowrap' }}>{iso && expLabel(iso)}</Box>
        {showPE && <Box sx={{ gridColumn: `span ${peCols}`, color: c.put, textAlign: 'right' }}>PUTS ●</Box>}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: tmpl, px: 0.5, pb: 0.4, fontSize: 10, color: c.faint, borderBottom: `1px solid ${c.border}` }}>
        {showCE && [extra ? extraLabel : null, 'Mark', 'Bid', 'Ask'].filter(Boolean).map(h => <Box key={`c${h}`} sx={{ textAlign: 'right', px: 0.6 }}>{h}</Box>)}
        <Box sx={{ textAlign: 'center' }}>Strike</Box>
        {showPE && ['Bid', 'Ask', 'Mark', extra ? extraLabel : null].filter(Boolean).map(h => <Box key={`p${h}`} sx={{ textAlign: 'right', px: 0.6 }}>{h}</Box>)}
      </Box>
      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', position: 'relative', px: 0.5 }}>
        {rows.map((row, i) => (
          <React.Fragment key={row.strike}>
            {i === idxAt && (
              <Box ref={idxRef} sx={{ position: 'relative', height: 0 }}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, top: -1, borderTop: `1px solid ${c.amber}`, zIndex: 1 }} />
                <Box sx={{ position: 'absolute', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 2, px: 1, py: 0.1, borderRadius: 3, border: `1px solid ${c.amber}`, bgcolor: c.panel, color: c.amber, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  INDEX {fmtStrike(spot)}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: tmpl, borderBottom: `1px solid ${c.border}`, '&:hover': { bgcolor: c.hover } }}>
              {showCE && sideCells(row, 'CE')}
              <Box sx={{
                height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, px: 0.2, overflow: 'hidden', fontWeight: 700,
                color: row.strike === atm ? c.amber : c.text,
                bgcolor: row.strike === atm ? c.amberBg : (c.isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)'),
                fontVariantNumeric: 'tabular-nums', position: 'relative',
              }}>
                {/* legs already in the basket: call side badge left, put side right */}
                {legBadges.get(`CE|${row.strike}`) && showCE && <Box component="span" sx={badgeSx('left')}>{legBadges.get(`CE|${row.strike}`)}</Box>}
                <Box component="span" sx={{ flexShrink: 0 }}>{fmtStrike(row.strike)}</Box>
                {legBadges.get(`PE|${row.strike}`) && showPE && <Box component="span" sx={badgeSx('right')}>{legBadges.get(`PE|${row.strike}`)}</Box>}
              </Box>
              {showPE && sideCells(row, 'PE')}
            </Box>
          </React.Fragment>
        ))}
        {idxAt === -1 && rows.length > 0 && <Box ref={idxRef} />}
      </Box>
    </Box>
  );
};

export default React.memo(ChainTable);
